import { useState } from 'react'
import { toast } from 'sonner'
import type { SelfTestReport } from '../../../types/selfTest'

interface CopyResultsButtonProps {
  report: SelfTestReport
}

/** Markdown summary, built so it can be pasted straight into a ticket or chat. */
export function buildReportMarkdown(report: SelfTestReport, includePasses = false): string {
  const { build, totals, latencyStats, pools } = report

  const header = [
    `IoAg API self-test — v${build.version} (${build.gitHash}${build.branch ? `, ${build.branch}` : ''}${
      build.buildTime ? `, built ${build.buildTime}` : ''
    })`,
    `${report.finishedAt} · ${(report.durationMs / 1000).toFixed(1)}s · ${totals.pass} pass / ${totals.fail} fail / ${totals.notCovered} not covered`,
    `Pools: main ${pools.main}, analytics ${pools.analytics} · instance ${report.instance.hostname}:${report.instance.port} (pid ${report.instance.pid})`,
    `Latency: p50 ${latencyStats.p50}ms · p95 ${latencyStats.p95}ms · max ${latencyStats.max}ms`,
  ].join('\n')

  const rows = report.results
    .filter((r) => (includePasses ? true : r.outcome !== 'pass'))
    .map((r) => {
      const label = r.outcome === 'fail' ? 'FAIL' : r.outcome === 'pass' ? 'PASS' : 'NOT '
      const status = r.status ? String(r.status) : '   '
      const latency = r.latencyMs !== undefined ? `${r.latencyMs}ms` : ''
      return `${label}  GET ${r.path}  ${status}  ${latency}  ${r.reason ?? ''}`.trimEnd()
    })

  return `${header}\n\n${rows.join('\n')}`
}

export function CopyResultsButton({ report }: CopyResultsButtonProps) {
  const [fallbackText, setFallbackText] = useState<string | null>(null)

  const handleCopy = async (includePasses: boolean) => {
    const markdown = buildReportMarkdown(report, includePasses)

    // Clipboard access requires a secure context; without it, render the text
    // so it can still be selected and copied by hand.
    if (!navigator.clipboard) {
      setFallbackText(markdown)
      return
    }

    try {
      await navigator.clipboard.writeText(markdown)
      toast.success('Copied to clipboard')
    } catch {
      setFallbackText(markdown)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <button
          onClick={() => handleCopy(false)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Copy issues
        </button>
        <button
          onClick={() => handleCopy(true)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Copy all
        </button>
      </div>

      {fallbackText && (
        <pre className="max-h-64 overflow-auto rounded bg-gray-50 p-3 text-xs text-gray-700">
          {fallbackText}
        </pre>
      )}
    </div>
  )
}
